import React from 'react';
import {
  Progress,
  Text,
  VStack,
  HStack,
  Icon,
  Spinner,
  Alert,
  AlertIcon,
  Box
} from '@chakra-ui/react';
import { FiCheck, FiAlertTriangle } from 'react-icons/fi';

const SyncProgressModal = ({ isOpen, onClose, progress }) => {
  const { isRunning, current, total, phase, message, percentage } = progress;

  // Debug logging
  console.log('🎭 SyncProgressModal render:', { 
    isOpen, 
    phase, 
    isRunning, 
    current, 
    total, 
    percentage 
  });

  const getPhaseIcon = () => {
    switch (progress.phase) {
      case 'checking':
        return <Spinner color="blue.500" />;
      case 'fetching':
        return <Spinner color="yellow.500" />;
      case 'syncing':
        return <Spinner color="green.500" />;
      case 'completed':
        return <Icon as={FiCheck} color="green.500" w={8} h={8} />;
      case 'error':
        return <Icon as={FiAlertTriangle} color="red.500" w={8} h={8} />;
      default:
        return <Spinner color="blue.500" />;
    }
  };

  const getPhaseColor = () => {
    switch (progress.phase) {
      case 'checking':
        return 'info';
      case 'fetching':
        return 'warning';
      case 'syncing':
        return 'info';
      case 'completed':
        return 'success';
      case 'error':
        return 'error';
      default:
        return 'info';
    }
  };

  const getPhaseTitle = () => {
    switch (progress.phase) {
      case 'checking':
        return 'Verificando base de datos...';
      case 'fetching':
        return 'Obteniendo facturas del proveedor...';
      case 'syncing':
        return `Sincronizando ${progress.current || 0} de ${progress.total || 0} facturas...`;
      case 'completed':
        return 'Sincronización completada exitosamente';
      case 'error':
        return 'Error en la sincronización';
      default:
        return 'Sincronizando facturas...';
    }
  };

  const shouldShowProgress = (phase === 'syncing' && total > 0) || (phase === 'completed' && total > 0);

  return (
    <>
      {isOpen && (
        <Box
          position="fixed"
          top="0"
          left="0"
          width="100vw"
          height="100vh"
          bg="rgba(0, 0, 0, 0.4)"
          zIndex="99999"
          display="flex"
          alignItems="center"
          justifyContent="center"
          data-sync-modal="true"
        >
          <Box
            bg="white"
            borderRadius="lg"
            maxW="600px"
            w="90%"
            maxH="80vh"
            overflow="auto"
            boxShadow="2xl"
            zIndex="100000"
          >
            {/* Header */}
            <Box p={6} borderBottom="1px solid" borderColor="gray.200">
              <HStack justify="space-between">
                <HStack>
                  {getPhaseIcon()}
                  <Text fontSize="lg" fontWeight="bold">Sincronización de Facturas</Text>
                </HStack>
                {(phase === 'completed' || phase === 'error') && (
                  <Box
                    as="button"
                    onClick={onClose}
                    p={2}
                    borderRadius="md"
                    _hover={{ bg: 'gray.100' }}
                    fontSize="lg"
                  >
                    ✕
                  </Box>
                )}
              </HStack>
            </Box>

            {/* Body */}
            <Box p={6}>
              <VStack spacing={6} align="stretch" width="100%">
                
                {/* Estado actual */}
                <Alert status={getPhaseColor()}>
                  <AlertIcon />
                  <VStack align="start" spacing={1} flex="1">
                    <Text fontSize="sm" fontWeight="medium">
                      {getPhaseTitle()}
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      {message}
                    </Text>
                  </VStack>
                </Alert>

          {/* Barra de progreso - mostrar cuando hay datos válidos */}
          {(shouldShowProgress || (phase === 'syncing' && percentage > 0)) && progress.phase !== 'error' && (
            <VStack spacing={4} align="stretch" width="100%">
              <HStack justify="space-between" width="100%">
                <Text fontSize="md" fontWeight="bold" color="blue.600">
                  Progreso de Sincronización
                </Text>
                <Text fontSize="lg" fontWeight="bold" color="green.600">
                  {current || 0} de {total || 0} ({Math.round(percentage || 0)}%)
                </Text>
              </HStack>
              
              <Box width="100%">
                <Progress 
                  key={`progress-${current}-${total}-${percentage}`}
                  value={percentage || 0} 
                  colorScheme={phase === 'completed' ? 'green' : 'blue'} 
                  size="xl"
                  height="24px"
                  width="100%"
                  borderRadius="lg"
                  hasStripe={phase === 'syncing'}
                  isAnimated={phase === 'syncing'}
                  bg="gray.200"
                  sx={{
                    width: '100%',
                    minWidth: '400px',
                    '& > div': {
                      transition: 'width 0.5s ease-in-out',
                      height: '24px'
                    }
                  }}
                />
              </Box>
              
              {/* Texto adicional debajo de la barra */}
              <Text fontSize="sm" color="gray.600" textAlign="center" fontStyle="italic">
                {phase === 'syncing' && 'Sincronizando facturas desde el servidor...'}
                {phase === 'completed' && '¡Sincronización completada exitosamente!'}
              </Text>
            </VStack>
          )}

          {/* Spinner para fases sin progreso específico */}
          {(phase === 'checking' || phase === 'fetching' || (phase === 'syncing' && total === 0)) && progress.phase !== 'error' && (
            <HStack justify="center" py={4}>
              <Spinner size="md" color={phase === 'fetching' ? 'orange.500' : 'blue.500'} />
              <Text fontSize="sm" color="gray.600">
                {phase === 'checking' && 'Consultando base de datos...'}
                {phase === 'fetching' && 'Descargando desde el proveedor...'}
                {phase === 'syncing' && 'Procesando...'}
              </Text>
            </HStack>
          )}              {/* Información adicional para primera sincronización */}
              {phase === 'syncing' && total > 50 && (
                <Alert status="info" size="sm">
                  <AlertIcon />
                  <Text fontSize="xs">
                    Esta es su primera sincronización. Puede tomar unos minutos...
                  </Text>
                </Alert>
              )}

              {/* Información sobre el tipo de sincronización */}
              {phase === 'fetching' && (
                <Alert status="info" size="sm">
                  <AlertIcon />
                  <Text fontSize="xs">
                    Conectando con el servidor de facturación electrónica...
                  </Text>
                </Alert>
              )}

              </VStack>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

export default SyncProgressModal;